<template>
  <v-container
    fluid
    class="intelligence-bid-audit pa-4"
    style="position: relative; min-height: 400px"
  >
    <v-row>
      <v-col cols="12">
        <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
          Project Staffing Audit
        </h1>
        <p class="text-subtitle-2 grey--text text--darken-1 mb-4">
          Upload a bid or statement of work, verify the roles and requirements,
          then assemble and audit a candidate team against them.
        </p>
      </v-col>
    </v-row>

    <v-stepper
      :value="currentStep"
      alt-labels
      flat
      class="elevation-0 mb-4"
      style="background: transparent"
    >
      <v-stepper-header style="box-shadow: none">
        <v-stepper-step :complete="currentStep > 1" step="1" color="#007AFF">
          Upload
        </v-stepper-step>
        <v-divider />
        <v-stepper-step :complete="currentStep > 2" step="2" color="#007AFF">
          Verify
        </v-stepper-step>
        <v-divider />
        <v-stepper-step :complete="currentStep > 3" step="3" color="#007AFF">
          Assemble
        </v-stepper-step>
        <v-divider />
        <v-stepper-step :complete="currentStep > 4" step="4" color="#10B981">
          Report
        </v-stepper-step>
      </v-stepper-header>
    </v-stepper>

    <v-alert v-if="hasError" type="error" class="mb-4" dismissible @input="clearError">
      {{ errorMessage }}
    </v-alert>

    <!-- Loading overlay (mirrors Team Builder's v-overlay pattern) -->
    <v-overlay :value="isUploading || isAuditing" absolute opacity="0.85" z-index="10">
      <v-card class="pa-6 text-center" style="max-width: 480px; border-radius: 16px" dark>
        <v-progress-circular
          indeterminate
          size="56"
          width="4"
          color="#007AFF"
          class="mb-4"
        />
        <h3 class="text-h6 font-weight-bold mb-2">
          {{ isUploading ? "Reading the bid document" : "Auditing the team" }}
        </h3>
        <p class="text-body-2 mb-3" style="opacity: 0.8">
          {{
            isUploading
              ? "Extracting roles, requirements, and constraints..."
              : "Scoring each person against the bid requirements..."
          }}
        </p>
        <v-progress-linear indeterminate color="#007AFF" rounded height="4" class="mb-2" />
        <p class="text-caption" style="opacity: 0.5">
          This typically takes a few seconds
        </p>
      </v-card>
    </v-overlay>

    <!-- STEP 1: Upload -->
    <div v-if="currentStep === 1">
      <v-row>
        <v-col cols="12" md="8">
          <v-card outlined class="pa-4 mb-4">
            <v-text-field
              v-model="title"
              label="Project / bid title"
              outlined
              dense
              hide-details
              color="#007AFF"
              class="mb-3"
              placeholder="e.g., Downtown Transit Hub - Structural Design"
            />

            <v-textarea
              v-model="projectContext"
              label="Project context (optional)"
              outlined
              dense
              rows="3"
              auto-grow
              hide-details
              color="#007AFF"
              class="mb-4"
              placeholder="Add any context that is not in the document itself: client, location, timeline, special constraints."
            />

            <h3 class="text-subtitle-2 font-weight-bold mb-2 theme-heading">
              Bid document
            </h3>

            <div
              class="dropzone pa-6 text-center mb-3"
              :class="{ 'dropzone-active': dragOver }"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onDrop"
              @click="$refs.fileInput.click()"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".pdf,.docx,.txt"
                style="display: none"
                @change="onFileChange"
              />
              <v-icon size="40" color="#007AFF" class="mb-2">mdi-cloud-upload-outline</v-icon>
              <div v-if="selectedFile" class="text-body-2 font-weight-bold theme-heading">
                {{ selectedFile.name }}
                <v-btn icon x-small color="#EF4444" class="ml-1" @click.stop="clearFile">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-else>
                <div class="text-body-2 font-weight-bold theme-heading">
                  Drag and drop a file here, or click to browse
                </div>
                <div class="text-caption grey--text">
                  Accepts PDF, DOCX, or TXT
                </div>
              </div>
            </div>

            <div class="text-caption grey--text text--darken-1 my-2 text-center">
              or paste the text directly
            </div>

            <v-textarea
              v-model="pastedText"
              label="Paste bid / SOW text"
              outlined
              dense
              rows="6"
              auto-grow
              hide-details
              color="#007AFF"
              class="mb-4"
              :disabled="!!selectedFile"
              placeholder="Paste the full text of the bid, RFP, or statement of work here."
            />

            <v-btn
              color="#007AFF"
              dark
              :loading="isUploading"
              :disabled="!canSubmitUpload"
              @click="submitUpload"
            >
              <v-icon left>mdi-file-search-outline</v-icon>
              Analyze Bid Document
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- STEP 2: Verify roles + requirements -->
    <div v-if="currentStep === 2 && bidDocument">
      <v-row>
        <v-col cols="12" md="9">
          <v-card v-if="bidDocument.summary" outlined class="pa-4 mb-4 summary-card">
            <div class="d-flex align-center mb-2">
              <v-icon small color="#3b82f6" class="mr-2">mdi-text-box-search-outline</v-icon>
              <span class="text-subtitle-2 font-weight-bold" style="color: #3b82f6">
                Bid Summary
              </span>
            </div>
            <p class="text-body-2 mb-0" style="line-height: 1.6">
              {{ bidDocument.summary }}
            </p>
          </v-card>

          <div
            v-if="bidDocument.constraints && bidDocument.constraints.length > 0"
            class="mb-4"
          >
            <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">
              Constraints
            </div>
            <div class="d-flex flex-wrap" style="gap: 8px">
              <v-chip
                v-for="(c, cIdx) in bidDocument.constraints"
                :key="'c-' + cIdx"
                small
                outlined
                color="#F59E0B"
              >
                <v-icon x-small left color="#F59E0B">mdi-alert-circle-outline</v-icon>
                {{ c }}
              </v-chip>
            </div>
          </div>

          <h3 class="text-subtitle-1 font-weight-bold mb-3 theme-heading">
            Roles ({{ roles.length }})
          </h3>

          <div
            v-for="(role, rIdx) in roles"
            :key="role.id || rIdx"
            class="role-card pa-4 mb-3"
          >
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <v-text-field
                  :value="role.title"
                  label="Role title"
                  outlined
                  dense
                  hide-details
                  color="#007AFF"
                  @input="setRoleField(rIdx, 'title', $event)"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-text-field
                  :value="role.count"
                  label="Count"
                  type="number"
                  min="1"
                  max="50"
                  outlined
                  dense
                  hide-details
                  color="#007AFF"
                  @input="setRoleField(rIdx, 'count', parseInt($event) || 1)"
                />
              </v-col>
              <v-col cols="6" sm="3">
                <v-select
                  :value="role.seniorityPreference"
                  :items="seniorityOptions"
                  label="Seniority"
                  outlined
                  dense
                  hide-details
                  clearable
                  color="#007AFF"
                  @input="setRoleField(rIdx, 'seniorityPreference', $event)"
                />
              </v-col>
            </v-row>

            <div
              v-if="role.reasoning"
              class="text-caption grey--text mb-2"
              style="font-style: italic"
            >
              {{ role.reasoning }}
            </div>

            <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">
              Requirements
            </div>

            <div
              v-for="(req, qIdx) in role.requirements"
              :key="qIdx"
              class="req-row d-flex align-center pa-2 mb-2"
            >
              <v-text-field
                :value="req.text"
                outlined
                dense
                hide-details
                color="#007AFF"
                class="flex-grow-1 mr-3"
                @input="setRequirementText(rIdx, qIdx, $event)"
              />

              <v-btn
                x-small
                depressed
                class="mr-3 importance-btn"
                :color="importanceBtnColor(req.importance)"
                dark
                @click="cycleImportance(rIdx, qIdx)"
              >
                {{ importanceLabel(req.importance) }}
              </v-btn>

              <div class="d-flex align-center mr-3" style="min-width: 130px">
                <v-slider
                  :value="req.weight"
                  min="0"
                  max="100"
                  hide-details
                  dense
                  color="#8b5cf6"
                  track-color="#e2e8f0"
                  style="max-width: 90px"
                  @input="setRequirementWeight(rIdx, qIdx, $event)"
                />
                <span
                  class="text-caption font-weight-bold ml-2"
                  :style="{
                    color: req.weight >= 70 ? '#8b5cf6' : '#94a3b8',
                    minWidth: '32px',
                    textAlign: 'right',
                  }"
                >
                  {{ req.weight }}%
                </span>
              </div>

              <v-btn
                icon
                x-small
                color="red lighten-2"
                @click="deleteRequirement(rIdx, qIdx)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>

            <v-btn small text color="#007AFF" @click="addRequirement(rIdx)">
              <v-icon small left>mdi-plus</v-icon>
              Add requirement
            </v-btn>
          </div>

          <div class="d-flex align-center mt-3" style="gap: 12px">
            <v-btn color="#007AFF" dark @click="setStep(3)">
              <v-icon left>mdi-account-multiple-plus</v-icon>
              Assemble Team
            </v-btn>
            <v-btn text color="grey darken-1" @click="setStep(1)">
              <v-icon small left>mdi-arrow-left</v-icon>
              Back
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- STEP 3: Assemble team -->
    <div v-if="currentStep === 3 && bidDocument">
      <v-row>
        <v-col cols="12" md="8">
          <v-card outlined class="pa-4 mb-4">
            <div class="d-flex align-center mb-3">
              <v-icon small color="#007AFF" class="mr-2">mdi-magnify</v-icon>
              <span class="text-subtitle-2 font-weight-bold theme-heading">
                Search the talent pool
              </span>
            </div>
            <div class="d-flex align-center mb-3">
              <v-text-field
                v-model="searchQuery"
                label="Search by skill, role, or keyword..."
                outlined
                dense
                hide-details
                color="#007AFF"
                class="mr-3"
                @keyup.enter="performSearch"
              />
              <v-btn color="#007AFF" dark :loading="isSearching" @click="performSearch">
                Search
              </v-btn>
            </div>

            <div v-if="searchResults.length > 0">
              <div
                v-for="result in searchResults"
                :key="result.profileId || result.name"
                class="d-flex align-center justify-space-between pool-row pa-2 mb-1"
              >
                <div>
                  <span class="text-body-2 font-weight-bold theme-heading">{{
                    result.name
                  }}</span>
                  <span v-if="result.currentTitle" class="text-caption grey--text ml-2">
                    {{ result.currentTitle }}
                  </span>
                </div>
                <v-btn
                  x-small
                  outlined
                  color="#007AFF"
                  :disabled="isSelected(result.profileId)"
                  @click="addFromResult(result)"
                >
                  <v-icon x-small left>mdi-plus</v-icon>
                  {{ isSelected(result.profileId) ? "Added" : "Add" }}
                </v-btn>
              </div>
            </div>

            <v-divider class="my-4" />

            <v-btn small outlined color="#007AFF" @click="openEmployeePicker">
              <v-icon small left>mdi-account-multiple-outline</v-icon>
              Browse employee list
            </v-btn>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined class="pa-4 mb-4">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="text-subtitle-2 font-weight-bold theme-heading">
                Selected team ({{ selectedProfiles.length }})
              </span>
            </div>

            <div v-if="selectedProfiles.length === 0" class="text-caption grey--text">
              No one added yet. Search or browse the employee list, then assign
              each person to a role.
            </div>

            <div
              v-for="profile in selectedProfiles"
              :key="profile.profileId"
              class="selected-row pa-2 mb-2"
            >
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="text-body-2 font-weight-bold theme-heading">{{
                  profile.name
                }}</span>
                <v-btn icon x-small color="#EF4444" @click="removeProfile(profile.profileId)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div v-if="profile.currentTitle" class="text-caption grey--text mb-1">
                {{ profile.currentTitle }}
              </div>
              <v-select
                :value="profile.roleId"
                :items="roleSelectItems"
                label="Assign to role"
                outlined
                dense
                hide-details
                color="#007AFF"
                @input="assignRole(profile.profileId, $event)"
              />
            </div>

            <v-divider class="my-3" />

            <v-switch
              v-model="wantSwapsLocal"
              label="Suggest swaps"
              color="#007AFF"
              hide-details
              dense
              class="mt-0 mb-3"
            />

            <v-btn
              block
              color="#10B981"
              dark
              :loading="isAuditing"
              :disabled="!canRunAudit"
              @click="runAudit"
            >
              <v-icon left>mdi-clipboard-check-outline</v-icon>
              Run Audit
            </v-btn>
            <div v-if="!canRunAudit" class="text-caption grey--text mt-2 text-center">
              Assign at least one person to a role to run the audit.
            </div>

            <div class="mt-3">
              <v-btn text small color="grey darken-1" @click="setStep(2)">
                <v-icon small left>mdi-arrow-left</v-icon>
                Back
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Employee list picker (reuses EmployeeList multi-select pattern) -->
      <v-dialog v-model="employeePicker" max-width="720" scrollable>
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Browse Employees
            <v-spacer />
            <v-btn icon @click="employeePicker = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text style="max-height: 520px">
            <v-text-field
              v-model="employeeSearch"
              label="Search by name, title, or location"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              clearable
              hide-details
              class="my-3"
            />
            <v-progress-linear v-if="loadingEmployees" indeterminate color="#007AFF" class="mb-2" />
            <div
              v-for="employee in filteredEmployees"
              :key="employee.id"
              class="d-flex align-center justify-space-between employee-pick-row pa-2 mb-1"
            >
              <div>
                <span class="text-body-2 font-weight-bold theme-heading">{{
                  employee.name || employee.username
                }}</span>
                <span v-if="employee.jobTitle" class="text-caption grey--text ml-2">
                  {{ employee.jobTitle }}
                </span>
              </div>
              <v-btn
                x-small
                outlined
                color="#007AFF"
                :disabled="isSelected(employee.id)"
                @click="addFromEmployee(employee)"
              >
                <v-icon x-small left>mdi-plus</v-icon>
                {{ isSelected(employee.id) ? "Added" : "Add" }}
              </v-btn>
            </div>
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text color="#007AFF" @click="employeePicker = false">Done</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <!-- STEP 4: Report (report left/main, Sage panel right; stacks on mobile) -->
    <div v-if="currentStep === 4 && auditResult">
      <v-row>
        <v-col cols="12" md="8">
          <BidAuditReport
            :audit-result="auditResult"
            :selected-profiles="selectedProfiles"
            :is-auditing="isAuditing"
            :view-state="reportViewState"
            @rerun="runAudit"
            @start-over="startOver"
            @export="exportReport"
            @view-profile="viewProfile"
            @add-swap="addSwapFromReport"
            @clear-view="reportViewState = null"
            @apply-view="reportViewState = $event"
          />
          <div class="mt-3 no-print">
            <v-btn text color="grey darken-1" @click="setStep(3)">
              <v-icon small left>mdi-arrow-left</v-icon>
              Back to team
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="4">
          <BidAuditSagePanel
            :bid-document="bidDocument"
            :audit-result="auditResult"
            :roles="roles"
            :is-auditing="isAuditing"
            @apply-edit="applyRequirementEdit"
            @add-swap-candidate="addSwapCandidate"
            @rerun="runAudit"
            @apply-view="reportViewState = $event"
          />
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
import * as searchApi from "../../api/IntelligenceSearchApi";
import * as companyApi from "../../api/CompanyApi";
import BidAuditReport from "../../components/CompanyView/BidAuditReport.vue";
import BidAuditSagePanel from "../../components/CompanyView/BidAuditSagePanel.vue";

const SENIORITY_OPTIONS = [
  { text: "Any", value: "" },
  { text: "Junior", value: "Junior" },
  { text: "Mid-Level", value: "Mid" },
  { text: "Senior", value: "Senior" },
  { text: "Lead", value: "Lead" },
  { text: "Principal", value: "Principal" },
  { text: "Director", value: "Director" },
];

export default {
  name: "IntelligenceBidAudit",
  components: { BidAuditReport, BidAuditSagePanel },
  data() {
    return {
      seniorityOptions: SENIORITY_OPTIONS,
      dragOver: false,
      selectedFile: null,
      // Display-only view state from Sage's re_rank_audit (sort/filter the
      // report). Never affects any score.
      reportViewState: null,
      // Step 3 search (uses the Smart Search endpoint directly so this view
      // does not collide with the shared intelligenceSearch store state).
      searchQuery: "",
      searchResults: [],
      isSearching: false,
      // Employee picker
      employeePicker: false,
      employees: [],
      employeeSearch: "",
      loadingEmployees: false,
    };
  },
  computed: {
    ...mapState("bidAudit", [
      "bidDocument",
      "selectedProfiles",
      "assignments",
      "auditResult",
      "currentStep",
      "isUploading",
      "isAuditing",
      "hasError",
      "errorMessage",
      "wantSwaps",
    ]),
    ...mapGetters("bidAudit", ["roles"]),
    title: {
      get() {
        return this.$store.state.bidAudit.title;
      },
      set(val) {
        this.updateField({ path: "title", value: val });
      },
    },
    projectContext: {
      get() {
        return this.$store.state.bidAudit.projectContext;
      },
      set(val) {
        this.updateField({ path: "projectContext", value: val });
      },
    },
    pastedText: {
      get() {
        return this.$store.state.bidAudit.pastedText;
      },
      set(val) {
        this.updateField({ path: "pastedText", value: val });
      },
    },
    wantSwapsLocal: {
      get() {
        return this.wantSwaps;
      },
      set(val) {
        this.setWantSwaps(val);
      },
    },
    canSubmitUpload() {
      return !!this.selectedFile || !!(this.pastedText && this.pastedText.trim());
    },
    roleSelectItems() {
      const items = this.roles.map((r) => ({
        text: r.title || "Untitled role",
        value: r.id,
      }));
      items.unshift({ text: "Unassigned", value: "" });
      return items;
    },
    canRunAudit() {
      return this.selectedProfiles.some((p) => p.roleId);
    },
    filteredEmployees() {
      if (!this.employeeSearch) return this.employees;
      const q = this.employeeSearch.toLowerCase();
      return this.employees.filter(
        (e) =>
          (e.name && e.name.toLowerCase().includes(q)) ||
          (e.jobTitle && e.jobTitle.toLowerCase().includes(q)) ||
          (e.location && e.location.toLowerCase().includes(q)),
      );
    },
  },
  methods: {
    ...mapMutations("bidAudit", [
      "updateField",
      "setStep",
      "clearError",
      "cycleRequirementImportance",
      "updateRequirementWeight",
      "updateRequirementText",
      "addRequirement",
      "removeRequirement",
      "updateRoleField",
      "addProfile",
      "removeProfile",
      "assignProfileToRole",
      "applyRequirementEdit",
      "setWantSwaps",
      "reset",
    ]),
    ...mapActions("bidAudit", ["uploadBidDocument", "runBidAudit"]),
    // ----- Step 1 -----
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.selectedFile = file;
    },
    onDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.selectedFile = file;
    },
    clearFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    submitUpload() {
      this.uploadBidDocument({ file: this.selectedFile });
    },
    // ----- Step 2 -----
    setRoleField(roleIndex, field, value) {
      this.updateRoleField({ roleIndex, field, value });
    },
    setRequirementText(roleIndex, reqIndex, text) {
      this.updateRequirementText({ roleIndex, reqIndex, text });
    },
    cycleImportance(roleIndex, reqIndex) {
      this.cycleRequirementImportance({ roleIndex, reqIndex });
    },
    setRequirementWeight(roleIndex, reqIndex, weight) {
      this.updateRequirementWeight({ roleIndex, reqIndex, weight });
    },
    deleteRequirement(roleIndex, reqIndex) {
      this.removeRequirement({ roleIndex, reqIndex });
    },
    importanceLabel(importance) {
      return importance === "nice_to_have" ? "Nice-to-Have" : "Must-Have";
    },
    importanceBtnColor(importance) {
      return importance === "nice_to_have" ? "#94a3b8" : "#ef4444";
    },
    // ----- Step 3: search -----
    async performSearch() {
      if (!this.searchQuery.trim()) return;
      this.isSearching = true;
      try {
        const result = await searchApi.SmartSearch(
          this.searchQuery.trim(),
          15,
          null,
          1,
          15,
          true,
        );
        if (result && result.success !== false) {
          const data = result.data || result;
          this.searchResults = data.results || [];
        }
      } finally {
        this.isSearching = false;
      }
    },
    isSelected(profileId) {
      return this.selectedProfiles.some((p) => p.profileId === profileId);
    },
    addFromResult(result) {
      this.addProfile({
        profileId: result.profileId,
        name: result.name,
        currentTitle: result.currentTitle || "",
        currentCompany: result.currentCompany || "",
      });
    },
    assignRole(profileId, roleId) {
      this.assignProfileToRole({ profileId, roleId });
    },
    // ----- Step 3: employee picker -----
    async openEmployeePicker() {
      this.employeePicker = true;
      if (this.employees.length === 0) {
        this.loadingEmployees = true;
        try {
          const result = await companyApi.GetAllEmployees();
          if (result && result.success) {
            this.employees = (result.data && result.data.employees) || [];
          }
        } finally {
          this.loadingEmployees = false;
        }
      }
    },
    addFromEmployee(employee) {
      this.addProfile({
        profileId: employee.id,
        name: employee.name || employee.username,
        currentTitle: employee.jobTitle || "",
        currentCompany: "",
      });
    },
    // ----- Step 3: audit -----
    runAudit() {
      this.runBidAudit();
    },
    startOver() {
      this.reset();
      this.selectedFile = null;
      this.searchQuery = "";
      this.searchResults = [];
      this.reportViewState = null;
    },
    // ----- Step 4: report -----
    viewProfile(match) {
      const id = match && (match.profileId || match.profile_id);
      if (!id) return;
      this.$router.push({
        name: "IntelligenceProfile",
        params: { profileId: id },
        query: { from: "bidaudit" },
      });
    },
    // Add a swap recommendation's candidate to the selected team, assigned to
    // the recommended role, so the user can re-run the audit with the swap.
    addSwapFromReport(swap) {
      if (!swap || !swap.candidate) return;
      const cand = swap.candidate;
      this.addProfile({
        profileId: cand.profileId,
        name: cand.name,
        currentTitle: cand.currentTitle || "",
        currentCompany: cand.currentCompany || "",
        roleId: swap.forRoleId || "",
      });
      this.assignProfileToRole({
        profileId: cand.profileId,
        roleId: swap.forRoleId || "",
      });
    },
    // Add a Sage suggest_swaps candidate to the team for a chosen role.
    addSwapCandidate({ candidate, roleId }) {
      if (!candidate) return;
      const profileId = candidate.profile_id || candidate.profileId;
      if (!profileId) return;
      this.addProfile({
        profileId,
        name: candidate.name,
        currentTitle: candidate.title || candidate.currentTitle || "",
        currentCompany: candidate.company || candidate.currentCompany || "",
        roleId: roleId || "",
      });
      this.assignProfileToRole({ profileId, roleId: roleId || "" });
    },
    // Export approach: a print-friendly export. The report markup hides
    // interactive controls under @media print (.no-print) and prints the
    // per-person fit, coverage, gaps, and swaps as a clean document. Chosen
    // over a generated docx for v1 — reliable, no new dependency, and reuses
    // the existing rendered report verbatim.
    exportReport() {
      window.print();
    },
  },
};
</script>

<style scoped>
.intelligence-bid-audit {
  max-width: 1400px;
}
.dropzone {
  border: 2px dashed #c5cae9;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
}
.dropzone:hover,
.dropzone-active {
  border-color: #007aff;
  background: #e3f2fd;
}
.role-card {
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.req-row {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.summary-card {
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.08),
    rgba(139, 92, 246, 0.05)
  );
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
}
.importance-btn {
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.05em;
  min-width: 90px;
}
.pool-row,
.selected-row,
.employee-pick-row {
  background: #f5f7fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}
</style>

<style>
.theme--dark .intelligence-bid-audit .dropzone {
  background: #2a2a3e;
  border-color: #444;
}
.theme--dark .intelligence-bid-audit .role-card,
.theme--dark .intelligence-bid-audit .req-row,
.theme--dark .intelligence-bid-audit .pool-row,
.theme--dark .intelligence-bid-audit .selected-row,
.theme--dark .intelligence-bid-audit .employee-pick-row {
  background: #2a2a3e;
  border-color: #444;
}
</style>
